/**
 * Auth utilities for API services
 * 
 * Provides helper functions to get authentication information from Clerk
 * for use in API services where hooks can't be used directly.
 */

/**
 * Get the current user ID from Clerk
 * @returns {string|null} User ID if authenticated, null otherwise
 */
export const getUserId = () => {
  // Get user ID from Clerk's frontend API
  try {
    if (typeof window !== 'undefined' && 
        window.Clerk && 
        window.Clerk.user && 
        window.Clerk.user.id) {
      // Only log once per session to avoid spam
      if (!window._clerkUserIdLogged) {
        console.log('Using authenticated user ID from Clerk');
        window._clerkUserIdLogged = true;
      }
      return window.Clerk.user.id;
    }
    
    // Only debug log if we haven't already shown this message
    if (typeof window !== 'undefined' && window.Clerk && !window._clerkDebugLogged) {
      console.debug('Clerk available but user ID not found:', {
        'Clerk.user exists': !!window.Clerk.user,
        'Clerk.user.id exists': window.Clerk.user ? !!window.Clerk.user.id : false,
      });
      window._clerkDebugLogged = true;
    }

    // If we need to support anonymous users, use the session ID
    const tempSessionId = localStorage.getItem('tempSessionId');
    if (tempSessionId) {
      console.log('Using temporary session ID');
      return tempSessionId;
    }
    
    console.warn('Unable to get user ID - user not authenticated and no temporary session');
    return null;
  } catch (error) {
    console.error('Error in getUserId:', error);
    return null;
  }
};

/**
 * Check if user is signed in with Clerk
 * @returns {boolean} True if signed in with Clerk
 */
export const isSignedIn = () => {
  return !!localStorage.getItem('clerk-user');
};

/**
 * Get the current user's email from Clerk
 * @returns {string|null} User email if available, null otherwise
 */
export const getUserEmail = () => {
  const clerkUser = localStorage.getItem('clerk-user');
  
  if (clerkUser) {
    try {
      const userData = JSON.parse(clerkUser);
      return userData.primaryEmailAddress?.emailAddress || null;
    } catch (error) {
      console.error('Error parsing Clerk user data:', error);
      return null;
    }
  }
  
  return null;
};

/**
 * Get the current user's name from Clerk
 * @returns {string|null} User name if available, null otherwise
 */
export const getUserName = () => {
  const clerkUser = localStorage.getItem('clerk-user');
  
  if (clerkUser) {
    try {
      const userData = JSON.parse(clerkUser);
      return userData.firstName || userData.username || null;
    } catch (error) {
      console.error('Error parsing Clerk user data:', error);
      return null;
    }
  }
  
  return null;
};
